import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, formValueSelector, getFormValues, FormSection } from 'redux-form';
import { TextField, BirthdayField, PhoneField } from './../../components/fields';
import { required, email } from './../../utilities/validation';
import WizardButtons from '../../components/wizard/navigator/buttons';
import { normalizeSSN, birthdayToDateString, maskSSN } from './../../utilities/string';
import { FormattedMessage } from 'react-intl';
import messages from '../../text/messages';
import { history } from './../../configuration/store';
import { updateUserDetails, updateUserProfile, getUserProfile } from '../../reducers/identity';
import { saveAddress, getAddresses } from '../../reducers/addresses';
import Address from '../../models/address';
import User from '../../models/user';
import { isAdmin } from '../../utilities/roles';
import { Snackbar } from '../../components/snackbar';
import { anythingChanged } from '../../utilities/common';
import AddressComponent from '../../components/address';

class ProfilePage extends React.Component {
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
    }

    submit(values) {
        const { role, profile, getUserProfile, getAddresses, updateUserProfile, saveAddress, reset } = this.props;
        const { firstName, lastName, middleName, noMiddleName, email, phoneNumber, dateOfBirth, socialSecurityNumber, mailingAddress } = values;

        let userProfile = new User(profile);
        userProfile.first_name = firstName;
        userProfile.no_middle_name = noMiddleName;
        userProfile.middle_name = middleName && middleName !== '' ? middleName : null;
        userProfile.last_name = lastName;
        userProfile.email = email;
        userProfile.phone = phoneNumber;
        userProfile.birthday_date = dateOfBirth;
        userProfile.ssn_bg_check = socialSecurityNumber;

        let persistAddress = () => {
            if (!isAdmin(role)) {
                let address = new Address(mailingAddress);
                saveAddress({ ...address, ref_type: role.type, ref_id: role.id }, () => {
                    getUserProfile(profile.id, reset);
                    getAddresses(role);
                });
            } else {
                getUserProfile(profile.id, reset);
            }

            Snackbar.success(<FormattedMessage id={'profileUpdated'} defaultMessage={'Profile details updated successfully.'} />);
        };

        updateUserProfile({ ...userProfile, birthday_date: birthdayToDateString(dateOfBirth) }, persistAddress);
    }

    componentDidMount() {
        const { role, addresses, getAddresses } = this.props;
        if (!addresses && !isAdmin(role)) {
            getAddresses(role);
        }
    }

    render() {
        const { handleSubmit, submitting, role, dateOfBirth, isPristine, initialValues } = this.props;

        return (
            <div className="page">
                <div className="row section">
                    <div className="col-xs-12">
                        <h4>{ProfilePage.meta.label}</h4>
                    </div>
                </div>

                <form onSubmit={handleSubmit(values => this.submit(values))}>
                    <div className="row section">
                        <div className="row">
                            <div className="col-xs-12 col-sm-9">
                                <div className="col-xs-12 col-sm-4">
                                    <Field name="firstName" validate={required} component={TextField} placeholder={messages.firstName} />
                                </div>
                                <div className="col-xs-12 col-sm-4">
                                    <Field name="middleName" component={TextField} placeholder={messages.middleName} />
                                </div>
                                <div className="col-xs-12 col-sm-4">
                                    <Field name="lastName" validate={required} component={TextField} placeholder={messages.lastName} />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-12 col-sm-9">
                                <div className="col-xs-12 col-sm-8">
                                    <Field
                                        name="email"
                                        id="profile-page-email"
                                        validate={[required, email]}
                                        component={TextField}
                                        placeholder={messages.email}
                                        disabled
                                    />
                                </div>
                                <div className="col-xs-12 col-sm-4">
                                    <Field name="phoneNumber" validate={required} component={PhoneField} placeholder={messages.phoneNumber} />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-12 col-sm-9">
                                <div className="col-xs-12 col-md-6">
                                    <Field
                                        name="dateOfBirth"
                                        validate={required}
                                        component={BirthdayField}
                                        selectedDate={dateOfBirth}
                                        placeholder={messages.dateOfBirth}
                                    />
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <Field
                                        name="socialSecurityNumber"
                                        validate={required}
                                        component={TextField}
                                        placeholder={messages.socialSecurityNumber}
                                        normalize={normalizeSSN}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isAdmin(role) && (
                        <div className="row section">
                            <div className="col-xs-12 col-sm-9">
                                <div className="row">
                                    <FormSection name={'mailingAddress'}>
                                        <AddressComponent
                                            form={'profile-page-form'}
                                            hideName={true}
                                            section={'mailingAddress'}
                                            countries={
                                                role.type === 'client'
                                                    ? null
                                                    : { [((initialValues || {}).mailingAddress || { country: 'US' }).country]: 'active' }
                                            }
                                            oneLineAddress={true}
                                            disabledFields={role.type === 'client' ? [] : ['country', 'state']}
                                        />
                                    </FormSection>
                                </div>
                            </div>
                        </div>
                    )}

                    <WizardButtons disabled={submitting || isPristine} submit={{ message: <FormattedMessage {...messages.update} /> }} />
                </form>
            </div>
        );
    }
}

ProfilePage.meta = {
    label: <FormattedMessage id={'personalDetails'} defaultMessage={'Personal Details'} />,
    icon: 'account_box',
    action: () => history.push('/profile'),
    locations: ['/profile']
};

ProfilePage = reduxForm({
    form: 'profile-page-form',
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true,
    forceUnregisterOnUnmount: false
})(ProfilePage);

const getInitialValues = state => {
    let profile = state.identity.profile;
    let addresses = state.addresses.list;
    let role = state.identity.role;

    if (!profile) {
        return undefined;
    }

    if (!addresses && !isAdmin(role)) {
        return undefined;
    }

    let personalInformation = {
        firstName: profile.first_name,
        middleName: profile.middle_name,
        lastName: profile.last_name,
        email: profile.email,
        phoneNumber: profile.phone,
        dateOfBirth: profile.birthday_date,
        socialSecurityNumber: maskSSN(profile.ssn_bg_check)
    };

    let dateOfBirth = { day: null, month: null, year: null };
    if (personalInformation.dateOfBirth) {
        let date = new Date(personalInformation.dateOfBirth + 'T00:00:00');
        dateOfBirth = {
            date: personalInformation.dateOfBirth,
            day: isNaN(date.getDate()) ? null : date.getDate(),
            month: isNaN(date.getMonth()) ? null : date.getMonth() + 1,
            year: isNaN(date.getFullYear()) ? null : date.getFullYear()
        };
    }

    let mailingAddress = undefined;
    if (addresses && addresses.length > 0) {
        mailingAddress = addresses[0];
    }

    return {
        ...personalInformation,
        dateOfBirth,
        mailingAddress
    };
};

const selector = formValueSelector('profile-page-form');
const mapStateToProps = state => {
    const initialValues = getInitialValues(state);
    const formValues = getFormValues('profile-page-form')(state);
    const isPristine = anythingChanged({ initialValues, values: formValues });

    return {
        role: state.identity.role,
        profile: state.identity.profile,
        addresses: state.addresses.list,
        userDetails: state.identity.details,
        dateOfBirth: selector(state, 'dateOfBirth') ? selector(state, 'dateOfBirth') : null,
        initialValues,
        isPristine
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getUserProfile: getUserProfile,
            updateUserDetails: updateUserDetails,
            updateUserProfile: updateUserProfile,
            saveAddress: saveAddress,
            getAddresses: getAddresses
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePage);



// WEBPACK FOOTER //
// ./src/pages/profile/index.jsx